import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Box,
  Avatar,
} from '@mui/material';
import {
  Dashboard,
  AdminPanelSettings,
  Logout,
  SportsEsports,
  Settings,
  AutoGraph,
} from '@mui/icons-material';

const SIDEBAR_WIDTH = 260;

export const Sidebar: React.FC = () => {
  const { logout, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: Dashboard },
    { path: '/adjust-config', label: 'Adjust Config', icon: Settings },
    { path: '/color-block-jam', label: 'Color-Block-Jam', icon: SportsEsports },
  ];

  return (
    <Box
      component="nav"
      sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        height: '100vh',
        background: 'linear-gradient(180deg, #1e1b4b 0%, #312e81 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AutoGraph sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
              Admin Panel
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Marketing Data
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, px: 2, py: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            fontWeight: 600,
            letterSpacing: '0.05em',
            px: 1.5,
            mb: 1,
            display: 'block',
          }}
        >
          MENU
        </Typography>
        <List sx={{ py: 0 }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  sx={{
                    borderRadius: 2,
                    py: 1.25,
                    backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: active ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Icon
                      sx={{
                        fontSize: 20,
                        color: active ? '#a5b4fc' : 'rgba(255,255,255,0.6)',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: active ? 600 : 500,
                      color: active ? '#fff' : 'rgba(255,255,255,0.8)',
                    }}
                  />
                  {active && (
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        borderRadius: 2,
                        backgroundColor: '#818cf8',
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}

          {role === 'super-admin' && (
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to="/super-admin"
                sx={{
                  borderRadius: 2,
                  py: 1.25,
                  backgroundColor: isActive('/super-admin') ? 'rgba(255,255,255,0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive('/super-admin')
                      ? 'rgba(255,255,255,0.15)'
                      : 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AdminPanelSettings
                    sx={{
                      fontSize: 20,
                      color: isActive('/super-admin') ? '#a5b4fc' : 'rgba(255,255,255,0.6)',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Super Admin"
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isActive('/super-admin') ? 600 : 500,
                    color: isActive('/super-admin') ? '#fff' : 'rgba(255,255,255,0.8)',
                  }}
                />
                {isActive('/super-admin') && (
                  <Box
                    sx={{
                      width: 4,
                      height: 20,
                      borderRadius: 2,
                      backgroundColor: '#818cf8',
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>

      {/* Footer - User & Logout */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.05)',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: '#818cf8',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {role === 'super-admin' ? 'SA' : 'A'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                {role === 'super-admin' ? 'Super Admin' : 'Admin'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {role}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Button
          fullWidth
          startIcon={<Logout sx={{ fontSize: 18 }} />}
          onClick={handleLogout}
          sx={{
            py: 1.25,
            borderRadius: 2,
            color: 'rgba(255,255,255,0.7)',
            backgroundColor: 'rgba(239,68,68,0.1)',
            justifyContent: 'flex-start',
            '&:hover': {
              backgroundColor: 'rgba(239,68,68,0.2)',
              color: '#fca5a5',
            },
          }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  );
};

export { SIDEBAR_WIDTH };
